/**
 * AIRefSlotCard
 *
 * Single slot card for an AI reference image.
 * Displays thumbnail, role badge, and action buttons.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { Plus, RefreshCw, X, Image as ImageIcon } from 'lucide-react';
import type { AIRefSlot } from '@/lib/api/products';
import { Button, Badge, Card, CardContent, Loading } from '@/components/v2/ui';
import type { RoleOption } from './RoleSelector';

// ============================================================================
// Types
// ============================================================================

interface AIRefSlotCardProps {
  slot: AIRefSlot;
  roles: RoleOption[];
  saving: boolean;
  onOpenPicker: () => void;
  onClear: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function AIRefSlotCard({ slot, roles, saving, onOpenPicker, onClear }: AIRefSlotCardProps) {
  const roleInfo = roles.find((r) => r.value.toLowerCase() === slot.role?.toLowerCase());

  return (
    <Card className={slot.is_empty ? 'border-dashed' : ''} style={{ minHeight: '180px' }}>
      <CardContent className="p-3 flex flex-col h-full">
        <SlotHeader slotNumber={slot.slot} roleInfo={roleInfo} />

        <div className="flex-grow flex items-center justify-center mb-2">
          {saving ? (
            <Loading size="sm" />
          ) : slot.is_empty ? (
            <EmptySlotButton onClick={onOpenPicker} />
          ) : (
            <SlotThumbnail slot={slot} />
          )}
        </div>

        {!slot.is_empty && !saving && (
          <SlotActions onOpenPicker={onOpenPicker} onClear={onClear} />
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function SlotHeader({ slotNumber, roleInfo }: { slotNumber: number; roleInfo?: RoleOption }) {
  return (
    <div className="flex justify-between items-center mb-2">
      <Badge variant="secondary">Slot {slotNumber}</Badge>
      {roleInfo && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${roleInfo.color}`}
        >
          <span>{roleInfo.icon}</span>
          {roleInfo.label}
        </span>
      )}
    </div>
  );
}

function EmptySlotButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="w-full h-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg hover:border-neutral-400 dark:hover:border-neutral-500 transition-colors"
      onClick={onClick}
      style={{ minHeight: '100px' }}
    >
      <Plus className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />
      <small className="text-neutral-600 dark:text-neutral-400">Bild waehlen</small>
    </button>
  );
}

function SlotThumbnail({ slot }: { slot: AIRefSlot }) {
  return (
    <div className="text-center w-full">
      <div
        className="bg-neutral-100 dark:bg-neutral-800 rounded mx-auto mb-2 overflow-hidden"
        style={{ width: '80px', height: '80px' }}
      >
        {slot.thumbnail_url ? (
          <img
            src={slot.thumbnail_url}
            alt={`Slot ${slot.slot}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />
          </div>
        )}
      </div>
      {slot.width && slot.height && (
        <small className="text-neutral-600 dark:text-neutral-400 text-xs block">
          {slot.width}&times;{slot.height}px
        </small>
      )}
    </div>
  );
}

function SlotActions({ onOpenPicker, onClear }: { onOpenPicker: () => void; onClear: () => void }) {
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="flex-grow" onClick={onOpenPicker}>
        <RefreshCw className="h-3 w-3 mr-1" />
        Aendern
      </Button>
      <Button variant="outline" size="sm" onClick={onClear}>
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
